import { useState } from "react";
import { motion } from "framer-motion";
import {
  Calculator, TrendingUp, Target, Sunset, GraduationCap, Receipt, BadgePercent, Gauge,
} from "lucide-react";
import {
  sipReport, lumpsumReport, goalSIP, retirementPlan, educationPlan, emiCalc, inflationCalc,
  formatINR, formatINRShort,
} from "@/lib/calculators";

const TABS = [
  { key: "sip", label: "SIP", icon: TrendingUp },
  { key: "lumpsum", label: "Lumpsum", icon: Calculator },
  { key: "goal", label: "Goal Planner", icon: Target },
  { key: "retirement", label: "Retirement", icon: Sunset },
  { key: "education", label: "Education", icon: GraduationCap },
  { key: "emi", label: "EMI", icon: Receipt },
  { key: "inflation", label: "Inflation", icon: BadgePercent },
];

export default function Calculators() {
  const [active, setActive] = useState("sip");

  return (
    <section id="tools" data-testid="calculators-section" className="section relative bg-[#080808]">
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs tracking-[0.25em] uppercase text-[#D4AF37]">
            <Gauge size={14} /> Smart Financial Tools
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mt-5 leading-[1.05]">
            Plan with <span className="text-gold-gradient">precision.</span>
          </h2>
          <p className="mt-5 text-gray-400 max-w-2xl mx-auto">
            Seven calculators to model your investments, retirement, education and loans — instantly.
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Financial calculators"
          data-testid="calc-tabs"
          className="flex flex-wrap justify-center gap-2 mb-10"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(t.key)}
                data-testid={`calc-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition border ${
                  isActive
                    ? "bg-[#D4AF37] text-black border-[#D4AF37] shadow-[0_8px_30px_-8px_rgba(212,175,55,0.6)]"
                    : "bg-white/5 text-gray-300 border-white/10 hover:border-[#D4AF37]/40 hover:text-white"
                }`}
              >
                <Icon size={15} />
                {t.label}
              </button>
            );
          })}
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="glass rounded-3xl p-6 md:p-10"
        >
          {active === "sip" && <SIPCalc />}
          {active === "lumpsum" && <LumpsumCalc />}
          {active === "goal" && <GoalCalc />}
          {active === "retirement" && <RetirementCalc />}
          {active === "education" && <EducationCalc />}
          {active === "emi" && <EMICalc />}
          {active === "inflation" && <InflationCalc />}
        </motion.div>

        <p className="mt-5 text-xs text-gray-500 text-center max-w-3xl mx-auto leading-relaxed">
          These calculators are for educational purposes only. Returns shown are not guaranteed.
          Mutual fund investments are subject to market risks. Read all scheme related documents carefully.
        </p>
      </div>
    </section>
  );
}

// --- Reusable input components ---
function Slider({ label, value, onChange, min, max, step = 1, format, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-gray-300">{label}</label>
        <span className="text-sm font-display font-semibold text-[#D4AF37]">
          {format ? format(value) : value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        aria-label={label}
        className="w-full accent-[#D4AF37] h-2"
      />
      <div className="flex justify-between text-[10px] text-gray-600 mt-1">
        <span>{format ? format(min) : min}</span>
        <span>{format ? format(max) : max}</span>
      </div>
    </div>
  );
}

function ResultCard({ label, value, accent = false, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl p-5 border ${
        accent
          ? "bg-gradient-to-br from-[#1A1407] to-[#0F0B05] border-[#D4AF37]/40"
          : "bg-black/30 border-white/10"
      }`}
    >
      <div className="text-xs tracking-[0.2em] uppercase text-gray-500">{label}</div>
      <div className={`mt-2 font-display font-bold text-2xl lg:text-3xl ${accent ? "text-gold-gradient" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

// --- Individual calculators ---
function SIPCalc() {
  const [monthly, setMonthly] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);
  const r = sipReport({ monthly, annualRate: rate, years });
  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div className="space-y-6">
        <h3 className="font-display font-bold text-2xl text-white">SIP Calculator</h3>
        <Slider label="Monthly investment" value={monthly} onChange={setMonthly} min={500} max={500000} step={500} format={formatINRShort} testId="sip-monthly" />
        <Slider label="Expected annual return (%)" value={rate} onChange={setRate} min={4} max={20} step={0.5} format={(v) => v + "%"} testId="sip-rate" />
        <Slider label="Investment period (years)" value={years} onChange={setYears} min={1} max={40} format={(v) => v + " yrs"} testId="sip-years" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ResultCard label="Invested" value={formatINRShort(r.invested)} testId="sip-result-invested" />
        <ResultCard label="Gain" value={formatINRShort(r.gain)} testId="sip-result-gain" />
        <div className="col-span-2">
          <ResultCard label="Future Value" value={formatINRShort(r.fv)} accent testId="sip-result-fv" />
        </div>
      </div>
    </div>
  );
}

function LumpsumCalc() {
  const [principal, setPrincipal] = useState(500000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);
  const r = lumpsumReport({ principal, annualRate: rate, years });
  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div className="space-y-6">
        <h3 className="font-display font-bold text-2xl text-white">Lumpsum Calculator</h3>
        <Slider label="One-time investment" value={principal} onChange={setPrincipal} min={10000} max={50000000} step={10000} format={formatINRShort} testId="lump-principal" />
        <Slider label="Expected annual return (%)" value={rate} onChange={setRate} min={4} max={20} step={0.5} format={(v) => v + "%"} testId="lump-rate" />
        <Slider label="Period (years)" value={years} onChange={setYears} min={1} max={40} format={(v) => v + " yrs"} testId="lump-years" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ResultCard label="Invested" value={formatINRShort(r.invested)} testId="lump-result-invested" />
        <ResultCard label="Gain" value={formatINRShort(r.gain)} testId="lump-result-gain" />
        <div className="col-span-2">
          <ResultCard label="Future Value" value={formatINRShort(r.fv)} accent testId="lump-result-fv" />
        </div>
      </div>
    </div>
  );
}

function GoalCalc() {
  const [target, setTarget] = useState(5000000);
  const [years, setYears] = useState(10);
  const [inflation, setInflation] = useState(6);
  const [rate, setRate] = useState(12);
  const r = goalSIP({ targetToday: target, years, inflation, annualRate: rate });
  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div className="space-y-6">
        <h3 className="font-display font-bold text-2xl text-white">Goal Planner</h3>
        <Slider label="Goal amount today" value={target} onChange={setTarget} min={100000} max={50000000} step={50000} format={formatINRShort} testId="goal-target" />
        <Slider label="Years to goal" value={years} onChange={setYears} min={1} max={40} format={(v) => v + " yrs"} testId="goal-years" />
        <Slider label="Inflation (%)" value={inflation} onChange={setInflation} min={3} max={12} step={0.5} format={(v) => v + "%"} testId="goal-inflation" />
        <Slider label="Expected return (%)" value={rate} onChange={setRate} min={4} max={20} step={0.5} format={(v) => v + "%"} testId="goal-rate" />
      </div>
      <div className="grid grid-cols-1 gap-3">
        <ResultCard label="Future goal cost" value={formatINRShort(r.futureTarget)} testId="goal-result-future" />
        <ResultCard label="Monthly SIP required" value={formatINR(r.monthly) + "/mo"} accent testId="goal-result-sip" />
        <ResultCard label="Total invested" value={formatINRShort(r.totalInvest)} testId="goal-result-total" />
      </div>
    </div>
  );
}

function RetirementCalc() {
  const [age, setAge] = useState(35);
  const [retire, setRetire] = useState(60);
  const [expense, setExpense] = useState(75000);
  const [life, setLife] = useState(85);
  const r = retirementPlan({
    currentAge: age, retireAge: retire, monthlyExpenseToday: expense, lifeExpectancy: life,
  });
  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div className="space-y-6">
        <h3 className="font-display font-bold text-2xl text-white">Retirement Calculator</h3>
        <Slider label="Current age" value={age} onChange={setAge} min={20} max={60} testId="ret-age" />
        <Slider label="Retirement age" value={retire} onChange={setRetire} min={age + 1} max={75} testId="ret-retire" />
        <Slider label="Monthly expense (today)" value={expense} onChange={setExpense} min={20000} max={1000000} step={5000} format={formatINRShort} testId="ret-expense" />
        <Slider label="Life expectancy" value={life} onChange={setLife} min={retire + 1} max={100} testId="ret-life" />
      </div>
      <div className="grid gap-3">
        <ResultCard label="Monthly expense at retirement" value={formatINRShort(r.expenseAtRetirement)} testId="ret-result-expense" />
        <ResultCard label="Retirement corpus needed" value={formatINRShort(r.corpusNeeded)} accent testId="ret-result-corpus" />
        <ResultCard label="Monthly SIP to start now" value={formatINR(r.monthlySIP) + "/mo"} testId="ret-result-sip" />
      </div>
    </div>
  );
}

function EducationCalc() {
  const [childAge, setChildAge] = useState(5);
  const [admit, setAdmit] = useState(18);
  const [cost, setCost] = useState(2500000);
  const r = educationPlan({ childAge, admitAge: admit, costToday: cost });
  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div className="space-y-6">
        <h3 className="font-display font-bold text-2xl text-white">Children's Education</h3>
        <Slider label="Child's current age" value={childAge} onChange={setChildAge} min={0} max={17} testId="edu-childage" />
        <Slider label="College admission age" value={admit} onChange={setAdmit} min={childAge + 1} max={25} testId="edu-admit" />
        <Slider label="Course cost today" value={cost} onChange={setCost} min={500000} max={20000000} step={50000} format={formatINRShort} testId="edu-cost" />
      </div>
      <div className="grid gap-3">
        <ResultCard label="Years to goal" value={`${r.years} yrs`} testId="edu-result-years" />
        <ResultCard label="Future cost" value={formatINRShort(r.futureCost)} accent testId="edu-result-future" />
        <ResultCard label="Monthly SIP required" value={formatINR(r.monthlySIP) + "/mo"} testId="edu-result-sip" />
      </div>
    </div>
  );
}

function EMICalc() {
  const [principal, setPrincipal] = useState(2500000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const r = emiCalc({ principal, annualRate: rate, years });
  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div className="space-y-6">
        <h3 className="font-display font-bold text-2xl text-white">EMI Calculator</h3>
        <Slider label="Loan amount" value={principal} onChange={setPrincipal} min={50000} max={100000000} step={50000} format={formatINRShort} testId="emi-principal" />
        <Slider label="Interest rate (%)" value={rate} onChange={setRate} min={4} max={20} step={0.1} format={(v) => v.toFixed(1) + "%"} testId="emi-rate" />
        <Slider label="Loan tenure (years)" value={years} onChange={setYears} min={1} max={30} testId="emi-years" />
      </div>
      <div className="grid gap-3">
        <ResultCard label="Monthly EMI" value={formatINR(r.emi)} accent testId="emi-result-emi" />
        <ResultCard label="Total interest" value={formatINRShort(r.totalInterest)} testId="emi-result-interest" />
        <ResultCard label="Total payable" value={formatINRShort(r.totalPayable)} testId="emi-result-total" />
      </div>
    </div>
  );
}

function InflationCalc() {
  const [amount, setAmount] = useState(100000);
  const [inflation, setInflation] = useState(6);
  const [years, setYears] = useState(15);
  const r = inflationCalc({ amount, inflation, years });
  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div className="space-y-6">
        <h3 className="font-display font-bold text-2xl text-white">Inflation Calculator</h3>
        <Slider label="Amount today" value={amount} onChange={setAmount} min={1000} max={10000000} step={1000} format={formatINRShort} testId="inf-amount" />
        <Slider label="Inflation rate (%)" value={inflation} onChange={setInflation} min={2} max={12} step={0.5} format={(v) => v + "%"} testId="inf-rate" />
        <Slider label="Number of years" value={years} onChange={setYears} min={1} max={40} testId="inf-years" />
      </div>
      <div className="grid gap-3">
        <ResultCard label="Amount needed in future" value={formatINRShort(r.future)} accent testId="inf-result-future" />
        <ResultCard label="Purchasing power erosion" value={r.erosionPct + "%"} testId="inf-result-erosion" />
        <div className="rounded-xl bg-black/30 border border-white/10 p-5 text-sm text-gray-400 leading-relaxed">
          What costs <span className="text-white font-semibold">{formatINRShort(amount)}</span> today will cost{" "}
          <span className="text-[#D4AF37] font-semibold">{formatINRShort(r.future)}</span> in {years} years at {inflation}% inflation.
        </div>
      </div>
    </div>
  );
}
